import { signal } from '@angular/core';

/** The browser's install prompt, as Chrome/Edge hand it to the page. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const captured = signal<BeforeInstallPromptEvent | null>(null);

/** The prompt the browser offered, once — until it is used or the app gets installed. */
export const installPrompt = captured.asReadonly();

let listening = false;

/**
 * OPS-03-T01: `beforeinstallprompt` can fire before Angular bootstraps, so the listener is attached as the first thing the app
 * does (called from `main.ts`). Preventing its default keeps Chrome's own mini-infobar out of the way; the header prompt asks
 * the user in the app's language and theme instead.
 */
export function captureInstallPrompt(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('beforeinstallprompt', (event: Event) => {
    event.preventDefault();
    captured.set(event as BeforeInstallPromptEvent);
  });
  window.addEventListener('appinstalled', () => captured.set(null));
}

/** Uses the prompt: the browser's own dialog decides, and the prompt cannot be offered twice. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = captured();
  if (!event) return 'unavailable';
  captured.set(null);
  await event.prompt();
  const choice = await event.userChoice;
  return choice.outcome;
}

/** True when the suite already runs as an installed app (no browser bar), so installing is not offered again. */
export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches === true;
  // iOS Safari has no display-mode for the home screen: `navigator.standalone` is the only signal there.
  return standalone || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}
